// FE-PINV-071 — Flatten Renewal Board response → List view rows.
//
// The List view is a flat sortable table over the same opportunity set
// the Grid + Kanban already render. No new BE round trip: walk the
// board's columns → cells → opportunities + the summary_row's
// closed_won/closed_lost entries (when show_closed=true).

import type {
  CadenceHealth,
  LivesBand,
  OpportunityStage,
  OpportunityType,
  RenewalBoardOpportunity,
  RenewalBoardResponse,
} from "../RenewalBoard/types";


/** A single row in the List view. ``last_activity_at`` and ``next_step``
 *  are optional because the Renewal Board response does not currently
 *  populate them per-opportunity; the renderer falls back to ``—``
 *  until a future BE pass extends the payload. */
export interface OpportunityListRow {
  opportunity_id: string;
  prospect_id: string;
  prospect_name: string;
  opportunity_name: string;
  stage: OpportunityStage;
  opportunity_type: OpportunityType;
  lives_band: LivesBand | null;
  estimated_lives: number | null;
  renewal_month: number;
  renewal_year: number;
  producer_id: string | null;
  value_estimate: number | null;
  cadence_health: CadenceHealth;
  days_until_renewal: number;
  form5500_alert_count: number;
  last_activity_at?: string | null;
  next_step?: string | null;
}


export interface FlattenOptions {
  show_closed: boolean;
}


function _toRow(
  opp: RenewalBoardOpportunity & {
    opportunity_name?: string;
    last_activity_at?: string | null;
    next_step?: string | null;
  },
): OpportunityListRow {
  return {
    opportunity_id: opp.opportunity_id,
    prospect_id: opp.prospect_id,
    prospect_name: opp.prospect_name,
    // The Renewal Board payload doesn't carry an opportunity name in
    // BE-SCRM-007's shape; the v1 surface labels rows by prospect.
    // Surface the prospect name as the opportunity label until the BE
    // adds an explicit ``opportunity_name`` field.
    opportunity_name: opp.opportunity_name ?? opp.prospect_name,
    stage: opp.stage,
    opportunity_type: opp.opportunity_type,
    lives_band: opp.lives_band,
    estimated_lives: opp.estimated_lives,
    renewal_month: opp.renewal_month,
    renewal_year: opp.renewal_year,
    producer_id: opp.producer_id,
    value_estimate: opp.value_estimate,
    cadence_health: opp.cadence_health,
    days_until_renewal: opp.days_until_renewal,
    form5500_alert_count: opp.form5500_alert_count,
    last_activity_at: opp.last_activity_at ?? null,
    next_step: opp.next_step ?? null,
  };
}


export function flattenBoardToListRows(
  board: RenewalBoardResponse | undefined,
  opts: FlattenOptions,
): OpportunityListRow[] {
  if (!board) return [];

  const rows: OpportunityListRow[] = [];
  const seen = new Set<string>();

  for (const col of board.columns) {
    for (const cell of col.cells) {
      // Cells with ``card_render_hint = count_tile`` still ship the
      // opportunities array; the cell modal owns the count-tile
      // explosion in Grid. For List, surface every opportunity
      // regardless of the cell's render hint.
      for (const opp of cell.opportunities) {
        if (seen.has(opp.opportunity_id)) continue;
        seen.add(opp.opportunity_id);
        rows.push(_toRow(opp));
      }
    }
  }

  if (opts.show_closed) {
    for (const opp of board.summary_row.closed_won.opportunities) {
      if (seen.has(opp.opportunity_id)) continue;
      seen.add(opp.opportunity_id);
      rows.push(_toRow(opp));
    }
    for (const opp of board.summary_row.closed_lost.opportunities) {
      if (seen.has(opp.opportunity_id)) continue;
      seen.add(opp.opportunity_id);
      rows.push(_toRow(opp));
    }
  }

  return rows;
}


/** Total opportunity count across the board, including the closed
 *  summary entries. Used for "Showing N of M" footer regardless of
 *  whether show_closed is on (M reflects the *available* pool). */
export function totalBoardOpportunityCount(
  board: RenewalBoardResponse | undefined,
): number {
  if (!board) return 0;
  let n = 0;
  for (const col of board.columns) {
    for (const cell of col.cells) {
      n += cell.count;
    }
  }
  n += board.summary_row.closed_won.count;
  n += board.summary_row.closed_lost.count;
  return n;
}
